<script setup>
  import { computed, watch } from "vue";
  import { useToast } from '@/composables/use-toast';
  
  const props = defineProps({
    criteria: Array
  });
  
  const emit = defineEmits(['normalize']);
  const { addToast } = useToast();
  
  const totalWeight = computed(() => {
    return (props.criteria || []).reduce((sum, c) => sum + (c.weight || 0), 0);
  });
  
  const roundedTotal = computed(() => {
    return Math.round(totalWeight.value * 100) / 100;
  });

  const isBalanced = computed(() => {
    return Math.abs(totalWeight.value - 100) < 0.01;
  });

  const isOverLimit = computed(() => {
    return roundedTotal.value > 100;
  });

  watch(roundedTotal, (newVal, oldVal) => {
    if (newVal > 100 && (oldVal === undefined || oldVal <= 100)) {
      addToast(`Weights exceed 100% (Current: ${newVal}%). Please adjust values.`, 'error');
    }
  });
  
  function formatNum(value) {
    if (value === undefined || value === null) return '0';
    return parseFloat(Number(value).toFixed(2));
  }
  
  function getColorByWeight(weight, index) {
    const colors = [
      '#fde725',
      '#dde318',
      '#bade28',
      '#95d840',
      '#75d054',
      '#56c667',
      '#3dbc74',
      '#29af7f',
      '#20a386',
      '#1f968b',
      '#238a8d',
      '#287d8e',
      '#2d718e',
      '#33638d',
      '#39558c',
      '#404688',
      '#453781',
      '#482576',
      '#481467',
      '#440154'
    ];
    
    if (Math.abs(weight - 100) < 0.01) {
      return '#56c667';
    }
    
    const colorIndex = index % 20;
    return colors[colorIndex];
  }
  
  const segments = computed(() => {
    const criteriaList = props.criteria || [];
    
    return criteriaList.map((c, idx) => {
      const weight = c.weight || 0;
      const percentage = totalWeight.value > 0 ? (weight / totalWeight.value) * 100 : 0;
      return {
        id: c.id || `criterion-${idx}`,
        name: c.name || 'Untitled',
        weight: weight,
        percentage: percentage,
        color: getColorByWeight(weight, idx)
      };
    });
  });
  
  function normalize() {
    emit('normalize');
  }
  </script>
  
  <template>
    <div 
      class="bg-echoes-background-black border rounded-xl p-4 transition-colors duration-300"
      :class="[
        isOverLimit 
          ? 'border-red-500 bg-red-500/5' 
          : 'border-echoes-border-gray'
      ]"
      data-testid="weighting-bar-container"
    >
      <div class="flex justify-between items-center mb-3">
        <div class="flex items-center gap-2">
          <span class="text-sm font-semibold transition-colors" :class="isOverLimit ? 'text-red-400' : 'text-white'">
            Weight Distribution
          </span>
          <v-tooltip location="top" max-width="300">
            <template v-slot:activator="{ props }">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" 
                class="w-4 h-4 cursor-help flex-shrink-0 transition-colors" 
                :class="isOverLimit ? 'text-red-400' : 'text-echoes-primary'"
                v-bind="props">
                <path fill="currentColor"
                  d="M12,2A10,10,0,1,0,22,12,10.01114,10.01114,0,0,0,12,2Zm0,18a8,8,0,1,1,8-8A8.00917,8.00917,0,0,1,12,20Zm0-8.5a1,1,0,0,0-1,1v3a1,1,0,0,0,2,0v-3A1,1,0,0,0,12,11.5Zm0-4a1.25,1.25,0,1,0,1.25,1.25A1,1.25,0,0,0,12,7.5Z">
                </path>
              </svg>
            </template>
            <span>How much each criterion contributes to the overall score. Weights across criteria must sum to 100%.</span>
          </v-tooltip>
        </div>
        <div class="flex items-center gap-2">
          <span :class="[
            'text-sm font-medium flex items-center gap-1 transition-colors',
            isBalanced ? 'text-green-400' : (isOverLimit ? 'text-red-400 font-bold' : 'text-echoes-primary')
          ]"
          data-testid="weights-sum-display">
            <i :class="[
              isBalanced ? 'mdi mdi-check' : 'mdi mdi-alert-outline',
              isOverLimit ? 'animate-pulse' : ''
            ]"></i>
            Weights sum to {{ formatNum(totalWeight) }}%
          </span>
          <button 
            v-if="!isBalanced && (props.criteria || []).length > 0" 
            class="rounded-lg px-3 py-1.5 cursor-pointer text-xs font-medium transition-all duration-200 border" 
            :class="[
              isOverLimit 
                ? 'bg-red-500/15 border-red-500/40 text-red-400 hover:bg-red-500/25' 
                : 'bg-echoes-primary/15 border-echoes-primary/40 text-echoes-primary hover:bg-echoes-primary/25'
            ]"
            @click="normalize"
            title="Auto-normalize weights to sum to 100%"
            data-testid="auto-normalize-button"
          >
            Auto-normalize
          </button>
        </div>
      </div>
      
      <div 
        class="flex h-8 rounded-lg overflow-hidden bg-black border mb-3 transition-colors"
        :class="isOverLimit ? 'border-red-500/50' : 'border-echoes-border-gray/50'"
        data-testid="weight-distribution-bar"
      >
        <v-tooltip 
          v-for="(segment, index) in segments" 
          :key="segment.id"
          location="top"
        >
          <template v-slot:activator="{ props: tooltipProps }">
            <div 
              v-bind="tooltipProps"
              class="flex items-center justify-center text-[10px] font-bold transition-all duration-200 relative cursor-pointer"
              :class="{ 'border-r border-black/40': index < segments.length - 1 }"
              :style="{ 
                width: segment.percentage + '%',
                backgroundColor: segment.color
              }"
              :data-testid="`weight-segment-${segment.id}`"
            >
              <span v-if="segments.length <= 16 && segment.percentage > 0" class="text-white text-sm font-bold drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]">
                {{ formatNum(segment.weight) }}%
              </span>
            </div>
          </template>
          <span class="font-medium">{{ formatNum(segment.weight) }}%</span>
        </v-tooltip>
      </div>
      
      <div class="flex flex-wrap gap-2">
        <div v-for="segment in segments" :key="`legend-${segment.id}`" class="flex items-center gap-1.5 text-xs">
          <span class="w-2.5 h-2.5 rounded-full border border-white/30 shadow-sm" :style="{ backgroundColor: segment.color }"></span>
          <span class="text-white/70">{{ segment.name }} ({{ formatNum(segment.weight) }}%)</span>
        </div>
      </div>
    </div>
  </template>